import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { BRAND } from '../config';
import { QRCodeSVG } from 'qrcode.react';

const Hostess = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sessionInfo, setSessionInfo] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', play_mode: 'whatsapp' });
  const [qty035, setQty035] = useState(0);
  const [qty120, setQty120] = useState(0);
  const [ticketPhoto, setTicketPhoto] = useState(null);
  const [ticketPreview, setTicketPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [gameToken, setGameToken] = useState(null);
  const [showQRCode, setShowQRCode] = useState(false);

  const fileInputRef = useRef(null);

  const loadSessionInfo = useCallback(async () => {
    try {
      const response = await api.get('/hostess/active-session', { params: { brand: BRAND } });
      setSessionInfo(response.data);
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur lors du chargement de la session' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessionInfo();
  }, [loadSessionInfo]);

  const handleSubmit = async (mode) => {
    // Validation: nom obligatoire
    if (!formData.name) {
      setMessage({ type: 'danger', text: 'Veuillez remplir le nom' });
      return;
    }

    const phoneDigits = (formData.phone || '').replace(/\D/g, '');
    if (!phoneDigits) {
      setMessage({ type: 'danger', text: 'Veuillez remplir le numéro de téléphone' });
      return;
    }
    if (!/^[267]\d{8}$/.test(phoneDigits)) {
      setMessage({ type: 'danger', text: 'Numéro invalide. Format attendu: 6XXXXXXXX (9 chiffres) (Cameroun).' });
      return;
    }
    const normalizedPhone = `+237${phoneDigits}`;

    // Validation: montant minimum 1000 FCFA
    const totalFcfa = qty035 * 250 + qty120 * 500;
    if (totalFcfa < 1000) {
      setMessage({ type: 'danger', text: 'Le montant total doit être d\'au moins 1000 FCFA pour participer' });
      return;
    }

    // Validation: photo du ticket obligatoire
    if (!ticketPhoto) {
      setMessage({ type: 'danger', text: 'Veuillez prendre une photo du ticket de caisse' });
      return;
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    const formDataObj = new FormData();
    formDataObj.append('name', formData.name);
    formDataObj.append('phone', normalizedPhone);
    formDataObj.append('play_mode', mode);
    formDataObj.append('qty_035', qty035);
    formDataObj.append('qty_120', qty120);
    formDataObj.append('brand', BRAND);
    if (ticketPhoto) {
      formDataObj.append('ticket_photo', ticketPhoto);
    }

    try {
      const response = await api.post('/hostess/register-participant', formDataObj);

      const token = response.data.game_token;

      if (mode === 'whatsapp') {
        // Mode WhatsApp: ouvrir directement WhatsApp avec le message pré-rempli
        const whatsappLink = response.data.whatsapp_link;

        console.log('WhatsApp mode - Token:', token);
        console.log('WhatsApp mode - Response:', response.data);

        setMessage({ type: 'success', text: `Participant enregistré !` });

        // Ouvrir WhatsApp dans un nouvel onglet
        if (whatsappLink) {
          window.open(whatsappLink, '_blank');
        }

        // Reset du formulaire
        setFormData({ name: '', phone: '', play_mode: 'whatsapp' });
        setQty035(0);
        setQty120(0);
        setTicketPhoto(null);
        setTicketPreview(null);
        loadSessionInfo();
      } else if (mode === 'qr_code') {
        // Mode QR Code: afficher le QR code à scanner
        console.log('QR Code mode - Token:', token);
        console.log('QR Code mode - Response:', response.data);

        if (token) {
          setGameToken(token);
          setShowQRCode(true);
          console.log('QR Code should show now');
          console.log('QR Code URL:', `https://americancola.spmerchflow.com/play/${token}`);
        } else {
          console.error('No token in response!');
          setMessage({ type: 'danger', text: 'Erreur: token de jeu non généré' });
        }
      } else {
        // Mode Direct: rediriger vers la page de jeu
        if (!token) {
          setMessage({ type: 'danger', text: 'Erreur: token de jeu non généré' });
          return;
        }
        setMessage({ type: 'success', text: 'Participant enregistré ! Redirection vers le jeu...' });
        setTimeout(() => {
          navigate(`/play/${token}`);
        }, 500);
        return;
      }
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur lors de l\'enregistrement' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleCloseQRCode = () => {
    setShowQRCode(false);
    setGameToken(null);
    setFormData({ name: '', phone: '', play_mode: 'whatsapp' });
    setQty035(0);
    setQty120(0);
    setTicketPhoto(null);
    setTicketPreview(null);
    loadSessionInfo();
  };

  // Gestion de la photo - Input file natif pour mobile
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setTicketPhoto(file);
      setTicketPreview(URL.createObjectURL(file));
    }
  };

  const clearPhoto = () => {
    if (window.confirm('Supprimer cette photo ?')) {
      setTicketPhoto(null);
      setTicketPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #bf0a30 0%, #3E2723 100%)' }}>
        <div className="text-center text-white">
          <div className="mb-4">
            <div style={{ width: '60px', height: '60px', margin: '0 auto', position: 'relative' }}>
              <div className="spinner-border" role="status" style={{ width: '100%', height: '100%', borderWidth: '4px' }}></div>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <i className="fas fa-wine-bottle" style={{ fontSize: '24px' }}></i>
              </div>
            </div>
          </div>
          <p className="mb-0 fw-bold" style={{ fontSize: 'clamp(14px, 3vw, 18px)' }}>CHARGEMENT...</p>
        </div>
      </div>
    );
  }

  if (!sessionInfo || !sessionInfo.active) {
    return (
      <div className="min-vh-100 d-flex flex-column" style={{ background: 'linear-gradient(135deg, #bf0a30 0%, #3E2723 100%)' }}>
        <nav className="navbar navbar-expand-lg navbar-dark" style={{ background: 'rgba(26, 26, 46, 0.5)', backdropFilter: 'blur(10px)' }}>
          <div className="container d-flex justify-content-center">
            <a className="navbar-brand fw-bold d-flex align-items-center" href="/">
              <img src="/logo.png" alt="American Cola" style={{ width: '120px', height: '120px' }} />
            </a>
          </div>
          <div className="container d-flex justify-content-end" style={{ position: 'absolute', top: '12px', right: '16px' }}>
            <span className="text-white me-2 d-none d-sm-inline">
              <i className="fas fa-user-tag me-1"></i>{user?.username}
            </span>
            <button onClick={handleLogout} className="btn btn-outline-light btn-sm" style={{ fontSize: '13px' }}>
              <i className="fas fa-sign-out-alt"></i>
            </button>
          </div>
        </nav>

        <div className="container flex-grow-1 d-flex align-items-center py-3">
          <div className="card border-0 shadow-lg w-100" style={{ borderRadius: '20px', background: 'rgba(26, 26, 46, 0.95)', border: '1px solid rgba(191, 10, 48, 0.3)' }}>
            <div className="card-body py-4 py-md-5 text-center">
              <div style={{ width: '70px', height: '70px', background: 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <i className="fas fa-calendar-times" style={{ fontSize: '28px', color: '#fff' }}></i>
              </div>
              <h3 className="mb-2 fw-bold text-white" style={{ fontSize: 'clamp(18px, 4vw, 24px)' }}>Aucune session active</h3>
              <p className="text-light mb-3" style={{ fontSize: 'clamp(13px, 2.5vw, 15px)' }}>
                Il n'y a pas de session de jeu active pour votre point de vente.<br />
                Veuillez contacter un administrateur.
              </p>
              <button onClick={loadSessionInfo} className="btn btn-lg px-4" style={{ background: 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)', color: '#fff', border: 'none', fontWeight: 'bold', fontSize: 'clamp(14px, 2.5vw, 16px)' }}>
                <i className="fas fa-sync-alt me-2"></i>Actualiser
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ background: 'linear-gradient(135deg, #bf0a30 0%, #3E2723 100%)' }}>
      {/* Header */}
      <nav className="navbar navbar-expand-lg navbar-dark sticky-top" style={{ background: 'rgba(26, 26, 46, 0.8)', backdropFilter: 'blur(10px)', padding: '8px 0', position: 'relative' }}>
        <div className="container d-flex justify-content-center">
          <a className="navbar-brand fw-bold d-flex align-items-center" href="/">
            <img src="/logo.png" alt="American Cola" style={{ width: '120px', height: '120px' }} />
          </a>
        </div>
        <div className="container d-flex justify-content-end" style={{ position: 'absolute', top: '8px', right: '16px' }}>
          <span className="text-white me-2 d-none d-md-block">
            <small style={{ fontSize: '10px', display: 'block' }}>Connecté en tant que</small>
            <strong style={{ fontSize: '13px' }}><i className="fas fa-user-tag me-1"></i>{user?.username}</strong>
          </span>
          <button onClick={handleLogout} className="btn btn-outline-light btn-sm" style={{ padding: '6px 10px', fontSize: '13px' }}>
            <i className="fas fa-sign-out-alt"></i>
          </button>
        </div>
      </nav>

      <div className="container py-3 py-md-4">
        {message.text && (
          <div className={'alert alert-' + (message.type === 'success' ? 'success' : 'danger') + ' alert-dismissible fade show mb-3 shadow-sm'} role="alert" style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
            <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'} me-2`}></i>
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })}></button>
          </div>
        )}

        {/* Session Info Bar - Responsive */}
        <div className="card border-0 shadow mb-3" style={{ borderRadius: '12px', background: 'rgba(26, 26, 46, 0.9)', border: '1px solid rgba(191, 10, 48, 0.3)' }}>
          <div className="card-body py-2 py-md-3">
            <div className="row align-items-center g-2 g-md-3">
              <div className="col-12 col-md-4">
                <div className="d-flex align-items-center">
                  <div style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                    <i className="fas fa-calendar-check" style={{ color: '#fff', fontSize: '14px' }}></i>
                  </div>
                  <div>
                    <small className="text-light d-block" style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Session</small>
                    <strong className="d-block text-white" style={{ fontSize: '13px' }}>{sessionInfo.session_name}</strong>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-4">
                <div className="d-flex align-items-center">
                  <div style={{ width: '36px', height: '36px', background: 'rgba(191, 10, 48, 0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                    <i className="fas fa-store" style={{ color: '#bf0a30', fontSize: '14px' }}></i>
                  </div>
                  <div>
                    <small className="text-light d-block" style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>PDV</small>
                    <strong className="d-block text-white" style={{ fontSize: '13px' }}>{sessionInfo.pdv_name}</strong>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-4">
                <div className="d-flex align-items-center">
                  <div style={{ width: '36px', height: '36px', background: 'rgba(191, 10, 48, 0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                    <i className="fas fa-clock" style={{ color: '#bf0a30', fontSize: '14px' }}></i>
                  </div>
                  <div>
                    <small className="text-light d-block" style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Horaires</small>
                    <strong className="d-block text-white" style={{ fontSize: '11px' }}>
                      {new Date(sessionInfo.start_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - {new Date(sessionInfo.end_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-3 g-md-4">
          {/* Registration Form */}
          <div className="col-12">
            <div className="card border-0 shadow" style={{ borderRadius: '12px', background: 'rgba(26, 26, 46, 0.9)', border: '1px solid rgba(191, 10, 48, 0.3)' }}>
              <div className="card-body p-3 p-md-4">
                <div className="d-flex align-items-center mb-3">
                  <div style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                    <i className="fas fa-user-plus" style={{ color: '#fff', fontSize: '14px' }}></i>
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold text-white" style={{ fontSize: 'clamp(14px, 2.5vw, 16px)' }}>Nouveau Participant</h6>
                    <small className="text-light" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>Enregistrez un client pour le jeu</small>
                  </div>
                </div>

                <form onSubmit={(e) => e.preventDefault()}>
                  {/* Photo Section - EN PREMIER */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-light" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>
                      <i className="fas fa-camera me-2" style={{ color: '#bf0a30' }}></i>1. Photo du ticket
                    </label>
                    {ticketPreview ? (
                      <div className="position-relative">
                        <div className="card border-2" style={{ borderColor: '#bf0a30', background: 'rgba(191, 10, 48, 0.1)' }}>
                          <div className="card-body p-2 text-center">
                            <img
                              src={ticketPreview}
                              alt="Ticket"
                              style={{ maxHeight: '150px', width: '100%', objectFit: 'contain', borderRadius: '8px' }}
                            />
                            <div className="mt-2">
                              <span className="badge" style={{ background: '#bf0a30', color: '#fff', fontSize: 'clamp(10px, 2vw, 12px)' }}>
                                <i className="fas fa-check-circle me-1"></i>Photo OK
                              </span>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={clearPhoto}
                          className="position-absolute top-0 end-0 btn btn-danger btn-sm shadow m-2"
                          title="Supprimer la photo"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="btn w-100 py-2 py-md-3 border-2"
                          style={{ background: 'transparent', borderColor: '#bf0a30', color: '#bf0a30', fontSize: 'clamp(13px, 2.5vw, 15px)' }}
                        >
                          <i className="fas fa-camera me-2"></i>
                          <span className="fw-semibold">Prendre une photo</span>
                        </button>
                        {/* Input file caché qui déclenche la caméra sur mobile */}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={handleFileSelect}
                          style={{ display: 'none' }}
                        />
                      </>
                    )}
                  </div>

                  {/* Informations du participant */}
                  <div className="row g-2 g-md-3 mb-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-light" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>2. Nom</label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ background: 'rgba(191, 10, 48, 0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#bf0a30', padding: '10px 12px' }}><i className="fas fa-user"></i></span>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Ex: Jean Dupont"
                          required
                          style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#fff', fontSize: 'clamp(13px, 2.5vw, 16px)' }}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-light" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>3. Téléphone</label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ background: 'rgba(191, 10, 48, 0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#bf0a30', padding: '10px 12px' }}><i className="fas fa-phone"></i></span>
                        <input
                          type="tel"
                          className="form-control"
                          placeholder="6XXXXXXXX (obligatoire)"
                          value={formData.phone}
                          onChange={(e) => {
                            // Numéro Cameroun: on garde les 9 derniers chiffres nationaux
                            let digits = (e.target.value || '').replace(/\D/g, '');
                            if (digits.startsWith('00237')) digits = digits.slice(5);
                            if (digits.startsWith('237')) digits = digits.slice(3);
                            digits = digits.slice(0, 9);
                            setFormData({ ...formData, phone: digits });
                          }}
                          style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(191, 10, 48, 0.3)', color: '#fff', fontSize: 'clamp(13px, 2.5vw, 16px)' }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Quantités de bouteilles */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-light" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>
                      4. Bouteilles achetées
                    </label>
                    <div className="row g-2">
                      {/* 0.35L */}
                      <div className="col-6">
                        <div className="card border-0" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(191,10,48,0.3)', borderRadius: '10px' }}>
                          <div className="card-body p-2 text-center">
                            <small className="text-light d-block mb-1" style={{ fontSize: 'clamp(10px, 2vw, 12px)' }}>Bouteille 0,35L</small>
                            <div className="d-flex align-items-center justify-content-center gap-2">
                              <button type="button" className="btn btn-sm" onClick={() => setQty035(q => Math.max(0, q - 1))}
                                style={{ background: 'rgba(191,10,48,0.2)', border: '1px solid rgba(191,10,48,0.3)', color: '#bf0a30', width: '32px', height: '32px', padding: 0 }}>
                                <i className="fas fa-minus" style={{ fontSize: '10px' }}></i>
                              </button>
                              <span className="text-white fw-bold" style={{ fontSize: 'clamp(16px, 3vw, 20px)', minWidth: '28px' }}>{qty035}</span>
                              <button type="button" className="btn btn-sm" onClick={() => setQty035(q => q + 1)}
                                style={{ background: 'rgba(191,10,48,0.2)', border: '1px solid rgba(191,10,48,0.3)', color: '#bf0a30', width: '32px', height: '32px', padding: 0 }}>
                                <i className="fas fa-plus" style={{ fontSize: '10px' }}></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* 1.20L */}
                      <div className="col-6">
                        <div className="card border-0" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(191,10,48,0.3)', borderRadius: '10px' }}>
                          <div className="card-body p-2 text-center">
                            <small className="text-light d-block mb-1" style={{ fontSize: 'clamp(10px, 2vw, 12px)' }}>Bouteille 1,20L</small>
                            <div className="d-flex align-items-center justify-content-center gap-2">
                              <button type="button" className="btn btn-sm" onClick={() => setQty120(q => Math.max(0, q - 1))}
                                style={{ background: 'rgba(191,10,48,0.2)', border: '1px solid rgba(191,10,48,0.3)', color: '#bf0a30', width: '32px', height: '32px', padding: 0 }}>
                                <i className="fas fa-minus" style={{ fontSize: '10px' }}></i>
                              </button>
                              <span className="text-white fw-bold" style={{ fontSize: 'clamp(16px, 3vw, 20px)', minWidth: '28px' }}>{qty120}</span>
                              <button type="button" className="btn btn-sm" onClick={() => setQty120(q => q + 1)}
                                style={{ background: 'rgba(191,10,48,0.2)', border: '1px solid rgba(191,10,48,0.3)', color: '#bf0a30', width: '32px', height: '32px', padding: 0 }}>
                                <i className="fas fa-plus" style={{ fontSize: '10px' }}></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Récapitulatif montant / tours */}
                    {(qty035 > 0 || qty120 > 0) && (() => {
                      const total = qty035 * 250 + qty120 * 500;
                      const tours = Math.floor(total / 1000);
                      const valid = total >= 1000;
                      return (
                        <div className="mt-2 p-2 rounded text-center" style={{ background: valid ? 'rgba(34,139,34,0.15)' : 'rgba(191,10,48,0.15)', border: `1px solid ${valid ? '#228b22' : '#bf0a30'}` }}>
                          <span className="fw-bold" style={{ color: valid ? '#6fcf6f' : '#ff6b6b', fontSize: 'clamp(13px, 2.5vw, 15px)' }}>
                            Total : {total} FCFA
                          </span>
                          {valid
                            ? <span className="text-light ms-2" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>→ {tours} tour{tours > 1 ? 's' : ''} de jeu</span>
                            : <span className="text-light ms-2" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>→ minimum 1000 FCFA requis</span>
                          }
                        </div>
                      );
                    })()}
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-12">
                      <label className="form-label fw-semibold small text-light" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>5. Mode de jeu</label>
                      <div className="row g-2">
                        <div className="col-4">
                          <button
                            type="button"
                            className="btn w-100 py-2 py-md-3"
                            disabled={submitting}
                            style={{
                              background: '#25D366',
                              color: '#fff',
                              border: '2px solid #25D366',
                              fontSize: 'clamp(11px, 2vw, 13px)',
                              fontWeight: 'bold',
                              borderRadius: '10px',
                              opacity: submitting ? 0.6 : 1
                            }}
                            onClick={() => handleSubmit('whatsapp')}
                          >
                            <i className="fab fa-whatsapp me-1"></i>
                            <div className="fw-semibold">WhatsApp</div>
                            <small style={{ fontSize: 'clamp(8px, 1.8vw, 10px)' }}>Envoyer au client</small>
                          </button>
                        </div>
                        <div className="col-4">
                          <button
                            type="button"
                            className="btn w-100 py-2 py-md-3"
                            disabled={submitting}
                            style={{
                              background: 'linear-gradient(135deg, #bf0a30 0%, #8c0824 100%)',
                              color: '#fff',
                              border: '2px solid #bf0a30',
                              fontSize: 'clamp(11px, 2vw, 13px)',
                              fontWeight: 'bold',
                              borderRadius: '10px',
                              opacity: submitting ? 0.6 : 1
                            }}
                            onClick={() => handleSubmit('direct')}
                          >
                            <i className="fas fa-mobile-alt me-1"></i>
                            <div className="fw-semibold">Jouer Direct</div>
                            <small style={{ fontSize: 'clamp(8px, 1.8vw, 10px)' }}>Sur ce mobile</small>
                          </button>
                        </div>
                        <div className="col-4">
                          <button
                            type="button"
                            className="btn w-100 py-2 py-md-3"
                            disabled={submitting}
                            style={{
                              background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
                              color: '#fff',
                              border: '2px solid #8B5CF6',
                              fontSize: 'clamp(11px, 2vw, 13px)',
                              fontWeight: 'bold',
                              borderRadius: '10px',
                              opacity: submitting ? 0.6 : 1
                            }}
                            onClick={() => handleSubmit('qr_code')}
                          >
                            <i className="fas fa-qrcode me-1"></i>
                            <div className="fw-semibold">QR Code</div>
                            <small style={{ fontSize: 'clamp(8px, 1.8vw, 10px)' }}>Scanner</small>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRCode && gameToken && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: '99999',
          padding: '20px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #bf0a30 0%, #3E2723 100%)',
            borderRadius: '20px',
            padding: '30px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center',
            border: '2px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 15px'
            }}>
              <i className="fas fa-qrcode" style={{ fontSize: '24px', color: '#fff' }}></i>
            </div>
            <h2 style={{
              color: '#fff',
              marginBottom: '10px',
              fontSize: 'clamp(18px, 4vw, 24px)',
              fontWeight: 'bold'
            }}>
              QR Code de Jeu
            </h2>
            <p style={{
              color: 'rgba(255, 255, 255, 0.9)',
              marginBottom: '20px',
              fontSize: 'clamp(13px, 2.5vw, 15px)'
            }}>
              Le client doit scanner ce QR code pour accéder à la carte à gratter
            </p>
            <div style={{
              background: '#fff',
              padding: '20px',
              borderRadius: '15px',
              marginBottom: '20px',
              display: 'inline-block'
            }}>
              <QRCodeSVG
                value={`https://americancola.spmerchflow.com/play/${gameToken}`}
                size={200}
                level="H"
                includeMargin={true}
              />
            </div>
            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => window.open(`https://americancola.spmerchflow.com/play/${gameToken}`, '_blank')}
                style={{
                  background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '12px 20px',
                  fontSize: 'clamp(12px, 2.5vw, 14px)',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <i className="fas fa-external-link-alt"></i>
                Tester le lien
              </button>
              <button
                onClick={handleCloseQRCode}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '10px',
                  padding: '12px 20px',
                  fontSize: 'clamp(12px, 2.5vw, 14px)',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                <i className="fas fa-times me-2"></i>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hostess;
